import type { ReactNode } from "react";
import { Box, Chip, Link, Stack, Typography } from "@mui/material";
import type { LaseredClaimantListItem, LaseredCondition, Policy, PolicyData } from "@/types";
import type { DeductibleResolutionEvent, DeductibleReview } from "@/types/deductibleReview";
import { ExpectedTriggerSummary } from "@/resources/policies/components/ExpectedTriggerSummary";
import { lasersOnPolicyPath } from "@/resources/claimants/components/ManageLasersOnPolicyLink";
import { buildConditionTimeline, clearedReason, type ConditionTimelineEntry } from "../conditionTimeline";
import { UserName } from "../deductible-review/DeductibleDecisionHistory";
import { candidateLabel } from "../deductible-review/reviewHelperText";
import { LASER_KIND_LABELS } from "@/utils/helpers/laserKind";
import { getResolvedClaimsValues } from "@/resources/claimants/hooks/useCurrentPolicyData";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import {
  chosenCandidate,
  contractFacts,
  dateWindow,
  day,
  money,
  reviewReasonText,
  stamp,
  straightLaserFacts,
} from "./deductibleRows";

// ─── Shared pieces ──────────────────────────────────────────────────────────

/** Label / value pairs in a two-column grid. */
export const Facts = ({ items }: { items: [string, ReactNode][] }) => (
  <Box
    component="dl"
    sx={{
      display: "grid",
      gridTemplateColumns: "150px minmax(0, 1fr)",
      columnGap: 1.5,
      rowGap: 0.5,
      m: 0,
      py: 0.75,
      fontSize: "0.8125rem",
    }}
  >
    {items.map(([label, value]) => (
      <Box key={label} sx={{ display: "contents" }}>
        <Typography component="dt" sx={{ fontSize: "inherit", color: "text.secondary" }}>
          {label}
        </Typography>
        <Typography component="dd" sx={{ fontSize: "inherit", fontWeight: 500, m: 0, minWidth: 0 }}>
          {value}
        </Typography>
      </Box>
    ))}
  </Box>
);

/** Dated lines, newest first. */
export const History = ({ lines, label }: { lines: { at?: string; text: ReactNode }[]; label: string }) => {
  if (lines.length === 0) return null;
  return (
    <Box
      component="ul"
      aria-label={label}
      sx={(t) => ({ listStyle: "none", m: 0, mt: 1, p: 0, borderTop: `1px dashed ${t.palette.divider}` })}
    >
      {lines.map((l, i) => (
        <Box
          component="li"
          key={i}
          sx={{ display: "flex", gap: 1.25, py: 0.75, fontSize: "0.75rem", color: "text.secondary" }}
        >
          <Box component="span" sx={{ minWidth: 92, color: "text.primary", fontWeight: 500 }}>
            {l.at ?? "—"}
          </Box>
          <Box component="span" sx={{ minWidth: 0 }}>
            {l.text}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

/** A quiet link: inherits colour, primary on hover, never bold. */
export const QuietLink = ({ href, children, outlined }: { href: string; children: ReactNode; outlined?: boolean }) => (
  <Link
    href={href}
    underline="none"
    sx={(t) => ({
      display: "inline-flex",
      alignItems: "center",
      height: 26,
      px: outlined ? 1.25 : 0.5,
      borderRadius: 1.5,
      border: outlined ? `1px solid ${t.palette.divider}` : "1px solid transparent",
      fontSize: "0.75rem",
      fontWeight: 500,
      color: "inherit",
      "&:hover": { color: t.palette.primary.main },
    })}
  >
    {children}
  </Link>
);

export const Actions = ({ children }: { children: ReactNode }) => (
  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
    {children}
  </Stack>
);

const CodeChips = ({ icd = [], cpt = [] }: { icd?: string[]; cpt?: string[] }) => (
  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap component="span">
    {icd.map((code) => (
      <Chip
        key={`icd-${code}`}
        label={`ICD ${code}`}
        size="small"
        variant="outlined"
        sx={{ height: 21, fontSize: "0.6875rem" }}
      />
    ))}
    {cpt.map((code) => (
      <Chip
        key={`cpt-${code}`}
        label={`CPT ${code}`}
        size="small"
        variant="outlined"
        sx={{ height: 21, fontSize: "0.6875rem" }}
      />
    ))}
  </Stack>
);

const claimLineHref = (id: string) => `#/claim-items/${id}/show`;
const manageLabel = "Manage on the policy";

// ─── Laser ──────────────────────────────────────────────────────────────────

export const LaserDetail = ({
  policyId,
  policyData,
  laserRow,
  effectiveDate,
  events,
}: {
  policyId: string;
  policyData?: PolicyData;
  laserRow?: LaseredClaimantListItem;
  effectiveDate?: string;
  events: DeductibleResolutionEvent[];
}) => {
  const facts = straightLaserFacts(policyData, laserRow);
  if (!facts) return null;
  const items: [string, ReactNode][] = [["Lasered deductible", money(facts.amount)]];
  if (facts.incremental > 0) items.push(["Incremental (aggregate only)", money(facts.incremental)]);
  items.push(["Effective", effectiveDate ? day(effectiveDate) : "Not on the claimant record"]);
  if (facts.reason) items.push(["Reason", facts.reason]);
  items.push(["Type", facts.laserType === "condition_based" ? "Condition-based (legacy)" : LASER_KIND_LABELS.standard]);
  const lines = events
    .filter((e) => e.source_after === "lasered")
    .map((e) => ({
      at: stamp(e.created_datetime),
      text: `Laser applied${e.effective_date ? ` from ${day(e.effective_date)}` : ""}: deductible ${money(
        e.amt_deductible_before
      )} → ${money(e.amt_deductible_after)}`,
    }));
  return (
    <>
      <Facts items={items} />
      <History label="Laser history" lines={lines} />
      <Actions>
        <QuietLink outlined href={`#${lasersOnPolicyPath(policyId)}`}>
          {manageLabel}
        </QuietLink>
      </Actions>
    </>
  );
};

// ─── Policy standard ────────────────────────────────────────────────────────

export const PolicyStandardDetail = ({ policy }: { policy?: Policy }) => {
  if (!policy) return null;
  const items: [string, ReactNode][] = [["Specific deductible", money(policy.specific_benefit?.specific_deductible)]];
  if (typeof policy.aggregate_benefit?.aggregate_deductible === "number")
    items.push(["Aggregate deductible", money(policy.aggregate_benefit.aggregate_deductible)]);
  items.push(["Term", dateWindow(policy.effective_start_date, policy.effective_end_date) ?? "—"]);
  if (policy.has_family_deductible_endorsement) items.push(["Family deductible", "Endorsement on file"]);
  return (
    <>
      <Facts items={items} />
      <Actions>
        <QuietLink outlined href={`#/policies/${policy.id}/show`}>
          Open the policy
        </QuietLink>
        <QuietLink href={`#/policies/${policy.id}/show/endorsements`}>Endorsements</QuietLink>
      </Actions>
    </>
  );
};

// ─── Conditional laser ──────────────────────────────────────────────────────

const timelineText = (t: ConditionTimelineEntry): ReactNode => {
  const line = t.triggeringClaimItemId ? (
    <Link href={claimLineHref(t.triggeringClaimItemId)} target="_blank" rel="noopener">
      claim
    </Link>
  ) : (
    "a claim"
  );
  const delta =
    t.amountAfter !== undefined && t.amountBefore !== t.amountAfter
      ? `: ${money(t.amountBefore)} → ${money(t.amountAfter)}`
      : "";
  switch (t.kind) {
    case "expected":
      return (
        <>
          Expected trigger from {day(t.effectiveDate)}
          {t.reason ? ` (${t.reason})` : ""}
          {t.by && (
            <>
              , set by <UserName value={t.by} />
            </>
          )}
          {delta}
        </>
      );
    case "replaced":
      return (
        <>
          Expected trigger replaced by {line}, date of service {day(t.effectiveDate)}
          {delta}
        </>
      );
    case "cancelled":
      return (
        <>
          Expected trigger cancelled{t.reason ? ` (${t.reason})` : ""}
          {t.by && (
            <>
              {" "}
              by <UserName value={t.by} />
            </>
          )}
        </>
      );
    case "overdue":
      return <>Past its review-by date ({day(t.effectiveDate)}) with no qualifying claim</>;
    case "triggered":
      return (
        <>
          {t.chosenInReview ? "Chosen in review" : "Triggered"} from {day(t.effectiveDate)}
          {delta}
          {t.triggeringClaimItemId && <> · {line}</>}
        </>
      );
    case "redated":
      return (
        <>
          Re-dated {t.direction} to {day(t.effectiveDate)} (was {day(t.previousEffectiveDate)})
          {t.chosenInReview ? ", chosen in review" : ""}
        </>
      );
    case "changed":
      return (
        <>
          Deductible changed{delta}
          {t.chosenInReview ? ", chosen in review" : ""}
        </>
      );
    case "reviewed":
      return <>Chosen in review: deductible stays {money(t.amountAfter)}</>;
    case "cleared":
      return (
        <>
          Cleared{delta}, {clearedReason(t.sourceAfter)}
        </>
      );
  }
};

export const ConditionDetail = ({
  condition: c,
  policyId,
  events,
}: {
  condition: LaseredCondition;
  policyId: string;
  events: DeductibleResolutionEvent[];
}) => {
  const triggered = c.lasered_condition_trigger_state === "triggered";
  const items: [string, ReactNode][] = [
    [triggered ? "Deductible" : "Deductible if triggered", money(c.amt_lasered_conditional_deductible)],
    ["Triggers on", <CodeChips key="codes" icd={c.icd_codes} cpt={c.cpt_codes} />],
    ["Incurred window", dateWindow(c.incurred_start_date, c.incurred_end_date) ?? "—"],
    ["Paid window", dateWindow(c.paid_start_date, c.paid_end_date) ?? "—"],
  ];
  if (triggered && c.triggered_by_claim_item_id) {
    items.push([
      "Triggered by",
      <Link key="line" href={claimLineHref(c.triggered_by_claim_item_id)} target="_blank" rel="noopener">
        claim{c.trigger_date ? `, date of service ${day(c.trigger_date)}` : ""}
      </Link>,
    ]);
  }
  items.push(["Expected trigger", c.expected_trigger ? <ExpectedTriggerSummary key="et" condition={c} /> : "Not set"]);
  const timeline = buildConditionTimeline(c.id, events, c);
  const lines = timeline.map((t) => ({ at: t.at ? stamp(t.at) : undefined, text: timelineText(t) }));
  lines.push({
    at: stamp(c.created_datetime),
    text: (
      <>
        Added
        {c.created_by && (
          <>
            {" "}
            by <UserName value={c.created_by} />
          </>
        )}
      </>
    ),
  });
  return (
    <>
      <Facts items={items} />
      <History label={`${c.lasered_condition_name} history`} lines={lines} />
      <Actions>
        <QuietLink outlined href={`#${lasersOnPolicyPath(policyId)}`}>
          {manageLabel}
        </QuietLink>
      </Actions>
    </>
  );
};

// ─── Carrier contract ───────────────────────────────────────────────────────

export const ContractDetail = ({ policyId, policyData }: { policyId: string; policyData?: PolicyData }) => {
  const f = contractFacts(policyData);
  if (!f) return null;
  const items: [string, ReactNode][] = [];
  if (f.lasered) items.push(["Contract deductible", money(f.amount)]);
  items.push(["Incurred window", f.incurred ?? "—"]);
  items.push(["Paid window", f.paid ?? "—"]);
  if (f.excludedProcedures.length > 0)
    items.push(["Excluded procedures", <CodeChips key="cpt" cpt={f.excludedProcedures} />]);
  // Plain YTD paid: no endpoint sends a contract-windowed total (calc expert, 2026-10-27).
  const spend = getResolvedClaimsValues(policyData)?.total_paid;
  if (f.lasered && typeof spend === "number") items.push(["YTD paid (all claims)", formatUSD(spend)]);
  return (
    <>
      <Facts items={items} />
      <Actions>
        <QuietLink outlined href={`#${lasersOnPolicyPath(policyId)}`}>
          {manageLabel}
        </QuietLink>
      </Actions>
    </>
  );
};

// ─── Deductible review ──────────────────────────────────────────────────────

export const ReviewDetail = ({
  review,
  conditions,
  claimantId,
}: {
  review: DeductibleReview;
  conditions: LaseredCondition[];
  claimantId: string;
}) => {
  const byId = new Map(conditions.map((c) => [c.id, c]));
  const chosen = chosenCandidate(review);
  const decidedAt = stamp(review.decided_datetime ?? review.resolved_datetime);
  const items: [string, ReactNode][] = [
    ["Opened", `${stamp(review.created_datetime) ?? "—"} · ${reviewReasonText(review).toLowerCase()}`],
    [
      "Candidates",
      (review.candidates ?? [])
        .map((c) => `${candidateLabel(c, byId)} ${money(c.amt_deductible)} (${day(c.effective_date) ?? "undated"})`)
        .join(" · ") || "—",
    ],
  ];
  if (review.status === "superseded") items.push(["Outcome", "Superseded by a newer review"]);
  else items.push(["Chosen", chosen ? `${candidateLabel(chosen, byId)} · ${money(chosen.amt_deductible)}` : "—"]);
  if (review.decision_reason) items.push(["Note", review.decision_reason]);
  const lines: { at?: string; text: ReactNode }[] = [];
  if (review.status === "resolved")
    lines.push({
      at: decidedAt,
      text: (
        <>
          Decided by <UserName value={review.decided_by} /> · claims released
        </>
      ),
    });
  if (review.status === "superseded")
    lines.push({ at: stamp(review.resolved_datetime) ?? undefined, text: "Superseded by a newer review" });
  lines.push({ at: stamp(review.created_datetime), text: "Review opened · claims held" });
  return (
    <>
      <Facts items={items} />
      <History label="Review history" lines={lines} />
      <Actions>
        <QuietLink href={`#/claimants/${claimantId}/show/audit`}>Audit log</QuietLink>
      </Actions>
    </>
  );
};
